/**
 * cameraBits — per-device camera-access bitmask math for user grants (JSUserDevice.bCameras).
 *
 * A grant's `bCameras` is a BIGINT UNSIGNED on the server (up to 64 cameras/device → bit 63),
 * carried to/from the client as a DECIMAL STRING because it exceeds JS's safe-integer range (2^53).
 * Camera N (1-based, = JSCamera.bCamera) maps to bit (N-1); the bit set ⇒ the user may touch that
 * camera. These helpers mirror the cloudapi backend (parseGrantsPayload / grantHasCamera) exactly, so
 * the UI and the server-side gate agree on the math. All functions are PURE and take/return the
 * decimal-string form (never a JS number — that would silently corrupt masks above bit 52).
 *
 * Invalid / empty input coerces to the empty mask ('0') rather than throwing, so a malformed row from
 * the backend can't crash the Setup screen. Camera numbers < 1 or non-integer contribute no bit.
 */

/* global BigInt */
// (BigInt is an ES2020 runtime global in every target browser + Node/jsdom; this directive just tells
//  the project's ESLint env — which predates ES2020 — that it exists, so `CI=true npm run build` passes.)

// Parse a mask (string|number|null) to a non-negative BigInt; anything unparseable → 0n.
const toBig = (mask) => {
  try {
    const b = BigInt(mask == null || mask === '' ? '0' : String(mask).trim());
    return b < 0n ? 0n : b;
  } catch (e) {
    return 0n;
  }
};

// Camera N (1-based) → its bit (1n << (N-1)); an invalid camera number contributes 0n.
const bitFor = (cam) => {
  const n = Number(cam);
  return Number.isInteger(n) && n >= 1 ? (1n << BigInt(n - 1)) : 0n;
};

/** Is camera `cam` accessible in this mask? */
export const hasCamera = (mask, cam) => (toBig(mask) & bitFor(cam)) !== 0n;

/** Return the mask (string) with camera `cam` set on (`on` truthy) or off. */
export const setCamera = (mask, cam, on) => {
  const bit = bitFor(cam);
  const b = toBig(mask);
  if (!bit) return String(b);
  return String(on ? (b | bit) : (b & ~bit));
};

/** Toggle camera `cam` in the mask (string in → string out). */
export const toggleCamera = (mask, cam) => setCamera(mask, cam, !hasCamera(mask, cam));

/** Build the mask (string) that grants exactly the given camera numbers (empty list → '0'). */
export const maskForCameras = (cams) =>
  String((cams || []).reduce((acc, c) => acc | bitFor(c), 0n));

/** Count of accessible cameras in the mask (popcount). */
export const cameraCount = (mask) => {
  let b = toBig(mask);
  let n = 0;
  while (b > 0n) {
    n += Number(b & 1n);
    b >>= 1n;
  }
  return n;
};

/** Are ALL of the given cameras accessible in this mask? (empty list → true) */
export const hasAllCameras = (mask, cams) => {
  const want = (cams || []).reduce((acc, c) => acc | bitFor(c), 0n);
  return (toBig(mask) & want) === want;
};

/** How many of the given cameras are accessible in this mask (for "N of M" summaries). */
export const countOf = (mask, cams) => (cams || []).reduce((n, c) => n + (hasCamera(mask, c) ? 1 : 0), 0);
